import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
    Autocomplete,
    TextField,
    InputAdornment,
    createFilterOptions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { createInvoice } from '../redux/ducks/invoices';
import { getClientBillingEntities } from '../redux/ducks/client';
import { getAccountBillingEntities } from '../redux/ducks/account';
import { getGroupBillingEntities } from '../redux/ducks/group';
import { getProjectBillingEntities } from '../redux/ducks/project';

const OptionBox = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const FormStack = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
}));

const HelperText = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const PeriodSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const PeriodLabel = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const PeriodModeToggle = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButton-root': {
        textTransform: 'none',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
}));

const RangeRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    '& > *': {
        flex: '1 1 160px',
    },
}));

const PeriodHelper = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
}));

const ENTITY_TYPES = ['Client', 'Account', 'Group', 'Project'];

const EMPTY_MESSAGE = {
    Client: 'No clients are configured as billing entities.',
    Account: 'No accounts are configured as billing entities.',
    Group: 'No groups are configured as billing entities. A group becomes one when it carries its own Retainer or Fixed Price billing terms.',
    Project: 'No projects are configured as billing entities. A project becomes one when it carries its own Fixed Price billing terms.'
};

const OPTION_LIMIT = 50;

const PICKER_HELPER = {
    Client: `Type part of the name to narrow the list. Every client invoices on its own. Only the first ${OPTION_LIMIT} matches are listed.`,
    Account: `Type part of the name to narrow the list. Only accounts marked as billing entities are listed, at most ${OPTION_LIMIT} at a time.`,
    Group: `Type part of the name to narrow the list. Only groups marked as billing entities are listed, at most ${OPTION_LIMIT} at a time.`,
    Project: `Type part of the name to narrow the list. Only projects marked as billing entities are listed, at most ${OPTION_LIMIT} at a time.`
};

const filterEntityOptions = createFilterOptions({ limit: OPTION_LIMIT, stringify: (option) => option.name || '' });

const formatCurrency = (n) => (typeof n === 'number' ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '');

const summarizeTerms = (terms) => {
    if (!terms?.type) return 'No billing terms set';
    if (terms.type === 'Retainer') {
        const hours = terms.hours ? `${terms.hours}h` : null;
        const charge = terms.charge ? `${formatCurrency(terms.charge)}/mo` : null;
        return ['Retainer', hours, charge].filter(Boolean).join(' · ');
    }
    return terms.type;
};

const previousMonthStart = () => dayjs().startOf('month').subtract(1, 'month');
const currentMonthStart = () => dayjs().startOf('month');
const earliestMonthStart = () => dayjs().startOf('month').subtract(24, 'month');

const isValidDay = (value) => !!value && dayjs.isDayjs(value) && value.isValid();

const NewInvoiceDialog = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [entityType, setEntityType] = useState('Client');
    const [entities, setEntities] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState('');

    const [periodMode, setPeriodMode] = useState('month');
    const [monthValue, setMonthValue] = useState(previousMonthStart);
    const [rangeStart, setRangeStart] = useState(null);
    const [rangeEnd, setRangeEnd] = useState(null);
    const [invoiceDateValue, setInvoiceDateValue] = useState(() => previousMonthStart().endOf('month'));

    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('warning');

    const showInlineAlert = (message, severity = 'warning') => {
        setAlertMessage(message);
        setAlertSeverity(severity);
    };

    const clearInlineAlert = () => {
        setAlertMessage('');
    };

    useEffect(() => {
        if (open) {
            loadEntities();
        }
    }, [open, entityType]);

    const loadEntities = () => {
        setLoading(true);
        setSelectedEntity('');
        clearInlineAlert();

        const receive = (billingEntities) => {
            setLoading(false);
            if (!billingEntities) {
                setEntities([]);
                showInlineAlert(`The list of ${entityType.toLowerCase()} billing entities could not be loaded. Close the dialog and try again.`, 'error');
                return;
            }
            const list = Array.isArray(billingEntities) ? [...billingEntities].sort((a, b) => (a.name || '').localeCompare(b.name || '')) : [];
            setEntities(list);
            if (list.length === 0) {
                showInlineAlert(EMPTY_MESSAGE[entityType], 'warning');
            }
        };

        switch (entityType) {
            case 'Client':
                dispatch(getClientBillingEntities(receive));
                break;

            case 'Account':
                dispatch(getAccountBillingEntities(receive));
                break;

            case 'Group':
                dispatch(getGroupBillingEntities(receive));
                break;

            case 'Project':
                dispatch(getProjectBillingEntities(receive));
                break;

            default:
                setLoading(false);
                setEntities([]);
        }
    };

    const handleEntityTypeChange = (e) => {
        setEntityType(e.target.value);
        setSelectedEntity('');
        clearInlineAlert();
    };

    const handlePeriodModeChange = (event, value) => {
        if (!value) return;
        setPeriodMode(value);
        clearInlineAlert();
    };

    const getSelectedEntity = () => {
        const entity = entities.find((e) => e._id === selectedEntity);
        return { type: entityType, id: selectedEntity, entity };
    };

    const monthTooLate = isValidDay(monthValue) && monthValue.startOf('month').isAfter(currentMonthStart());
    const isMonthValid = isValidDay(monthValue) && !monthTooLate;
    const rangeOutOfOrder = isValidDay(rangeStart) && isValidDay(rangeEnd) && rangeStart.startOf('day').isAfter(rangeEnd.startOf('day'));
    const isRangeValid = isValidDay(rangeStart) && isValidDay(rangeEnd) && !rangeOutOfOrder;
    const isPeriodValid = periodMode === 'month' ? isMonthValid : isRangeValid;

    const periodDescription = () => {
        if (periodMode === 'month') {
            if (monthTooLate) return 'Pick a month that has already started. Future months cannot be billed.';
            if (!isMonthValid) return 'Pick the month you want to bill.';
            return `Unbilled time, charges and Due payments dated in ${monthValue.format('MMMM YYYY')} will go on this invoice. Anything outside that month stays for another run.`;
        }
        if (rangeOutOfOrder) return 'The first date has to come before the last date.';
        if (!isRangeValid) return 'Pick the first and last day of the stretch you want to bill.';
        return `Unbilled time, charges and Due payments dated from ${rangeStart.format('MMMM D, YYYY')} through ${rangeEnd.format('MMMM D, YYYY')} will go on this invoice. Anything outside those dates stays for another run.`;
    };

    const handleCreate = () => {
        const chosen = getSelectedEntity();

        if (!chosen || !chosen.id) {
            showInlineAlert('Pick the billing entity to invoice.', 'warning');
            return;
        }

        const isBillingEntity = chosen.entity?.billingTerms?.billingEntity === true;
        if (!isBillingEntity) {
            showInlineAlert(`This ${entityType} is not configured as a billing entity`, 'warning');
            return;
        }

        if (!isPeriodValid) {
            showInlineAlert(periodDescription(), 'warning');
            return;
        }
        if (!isValidDay(invoiceDateValue)) {
            showInlineAlert('Pick the date this invoice should carry.', 'warning');
            return;
        }

        setCreating(true);
        clearInlineAlert();

        const invoiceData = {
            entityType: chosen.type,
            entityId: chosen.id,
            ...(periodMode === 'month'
                ? { month: monthValue.format('YYYY-MM') }
                : { startDate: rangeStart.format('YYYY-MM-DD'), endDate: rangeEnd.format('YYYY-MM-DD') }),
            ...(isValidDay(invoiceDateValue) ? { invoiceDate: invoiceDateValue.format('YYYY-MM-DD') } : {})
        };

        dispatch(createInvoice(invoiceData, (response, errorMessage) => {
            setCreating(false);
            if (response && response._id) {
                onClose();
                navigate(`/billing/invoices/${response._id}`);
            } else if (response && response.message) {
                showInlineAlert(response.message, 'info');
            } else {
                showInlineAlert(errorMessage || 'The invoice could not be created. Nothing was billed.', 'error');
            }
        }));
    };

    const handleClose = () => {
        if (!creating) {
            setEntityType('Client');
            setSelectedEntity('');
            setPeriodMode('month');
            setMonthValue(previousMonthStart());
            setRangeStart(null);
            setRangeEnd(null);
            setInvoiceDateValue(previousMonthStart().endOf('month'));
            clearInlineAlert();
            setAlertSeverity('warning');
            onClose();
        }
    };

    const isValid = () => !!selectedEntity && isPeriodValid;

    const selectedOption = entities.find((e) => e._id === selectedEntity) || null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Create New Invoice</DialogTitle>
            <DialogContent>
                <FormStack>
                    {alertMessage && (
                        <Alert severity={alertSeverity} onClose={clearInlineAlert}>
                            {alertMessage}
                        </Alert>
                    )}

                    <FormControl fullWidth required>
                        <InputLabel>Billing Entity Type</InputLabel>
                        <Select
                            value={entityType}
                            onChange={handleEntityTypeChange}
                            label="Billing Entity Type"
                        >
                            {ENTITY_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Autocomplete
                        options={entities}
                        loading={loading}
                        disabled={loading}
                        value={selectedOption}
                        onChange={(event, option) => {
                            setSelectedEntity(option?._id || '');
                            clearInlineAlert();
                        }}
                        getOptionLabel={(option) => option.name || ''}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        filterOptions={filterEntityOptions}
                        noOptionsText={EMPTY_MESSAGE[entityType]}
                        loadingText={`Loading ${entityType.toLowerCase()}s…`}
                        renderOption={(props, option) => (
                            <li {...props} key={option._id}>
                                <OptionBox>
                                    <Typography variant="body2">{option.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {summarizeTerms(option.billingTerms)}
                                    </Typography>
                                </OptionBox>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                required
                                label={`Select ${entityType}`}
                                placeholder={`Type to search ${entityType.toLowerCase()}s`}
                                helperText={PICKER_HELPER[entityType]}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? (
                                                <InputAdornment position="end">
                                                    <CircularProgress size={16} />
                                                </InputAdornment>
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />

                    <PeriodSection>
                        <PeriodLabel variant="subtitle2">Period to bill</PeriodLabel>
                        <PeriodModeToggle
                            size="small"
                            exclusive
                            value={periodMode}
                            onChange={handlePeriodModeChange}
                            aria-label="period to bill"
                        >
                            <ToggleButton value="month">Whole month</ToggleButton>
                            <ToggleButton value="range">Date range</ToggleButton>
                        </PeriodModeToggle>

                        {periodMode === 'month' ? (
                            <DatePicker
                                label="Month"
                                views={['year', 'month']}
                                openTo="month"
                                format="MMMM YYYY"
                                value={monthValue}
                                minDate={earliestMonthStart()}
                                maxDate={currentMonthStart()}
                                onChange={(value) => {
                                    setMonthValue(value);
                                    if (isValidDay(value)) setInvoiceDateValue(value.endOf('month'));
                                    clearInlineAlert();
                                }}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                            />
                        ) : (
                            <RangeRow>
                                <DatePicker
                                    label="First day"
                                    format="MMMM D, YYYY"
                                    value={rangeStart}
                                    maxDate={isValidDay(rangeEnd) ? rangeEnd : undefined}
                                    onChange={(value) => {
                                        setRangeStart(value);
                                        clearInlineAlert();
                                    }}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                                <DatePicker
                                    label="Last day"
                                    format="MMMM D, YYYY"
                                    value={rangeEnd}
                                    minDate={isValidDay(rangeStart) ? rangeStart : undefined}
                                    onChange={(value) => {
                                        setRangeEnd(value);
                                        if (isValidDay(value)) setInvoiceDateValue(value);
                                        clearInlineAlert();
                                    }}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </RangeRow>
                        )}

                        <DatePicker
                            label="Invoice date"
                            format="MMMM D, YYYY"
                            value={invoiceDateValue}
                            onChange={(value) => {
                                setInvoiceDateValue(value);
                                clearInlineAlert();
                            }}
                            slotProps={{ textField: { fullWidth: true, size: 'small', error: !isValidDay(invoiceDateValue), helperText: 'Defaults to the last day of the period being billed.' } }}
                        />

                        <PeriodHelper variant="caption">{periodDescription()}</PeriodHelper>
                    </PeriodSection>

                    <HelperText variant="body2" color="text.secondary">
                        Only entities configured as billing entities can generate invoices. If this entity was already invoiced for the period you
                        pick, no second invoice is created.
                    </HelperText>
                </FormStack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={creating}>
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    disabled={!isValid() || creating || loading}
                >
                    {creating ? <CircularProgress size={20} /> : 'Create Invoice'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewInvoiceDialog;
